import { useEffect, useMemo, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Banknote, CreditCard, Globe, WifiOff, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatMoney } from '@/lib/currency'
import type { OfflinePayment } from '@/lib/offline/offlineStore'

export type OfflineTender = 'cash' | 'card' | 'online'

type Props = {
  open: boolean
  /** The ticket the kitchen slip printed, e.g. "OFF-A3F1-7". */
  ticketRef: string
  /**
   * Totals for a given tender. A function, not a number, because the TENDER
   * DECIDES THE RATE — `tax_rate_cash` / `tax_rate_card` / `tax_rate_online` are
   * separate settings, so Cash and Card are genuinely different amounts due.
   * Showing one figure and charging another is the failure this shape prevents.
   */
  price: (tender: OfflineTender) => { total: number; unknownLines: number }
  /**
   * Cached online providers (Easypaisa/JazzCash/…). Empty = plain "online".
   * The slug is what the server validates; the label is what the cashier reads.
   */
  onlineSubMethods?: Array<{ slug: string; label: string }>
  /**
   * F-MM-N23: the held order is marketplace-settled. Only the Online tender
   * is allowed and the provider is fixed to the one named after the order
   * type; the server rejects anything else at replay, so refuse it here
   * rather than park the cashier's mistake in the reconciliation queue.
   */
  marketplaceLock?: { label: string; providerSlug?: string }
  onClose: () => void
  onTake: (payment: OfflinePayment) => void
  busy?: boolean
}

const TENDERS: Array<{ id: OfflineTender; label: string; Icon: typeof Banknote }> = [
  { id: 'cash', label: 'Cash', Icon: Banknote },
  { id: 'card', label: 'Card', Icon: CreditCard },
  { id: 'online', label: 'Online', Icon: Globe },
]

/**
 * Take payment for an order rung up during an outage (F-CTRL-N55).
 *
 * It opens from the held-orders tray, on demand — never by itself. The first
 * version popped automatically the moment a ticket fired, which made it an
 * ambush: the cashier could not start the next order, could not look at another
 * ticket, and on a short window could not even reach its buttons. Payment is an
 * action you choose, on an order you picked, like every other checkout here.
 *
 * WHAT THIS SCREEN IS NOT DECIDING
 *
 * The total is what this terminal computes from its cached menu, and it is what
 * the customer is asked for. It is not what the order settles at: on reconnect
 * the server reprices from the live menu and its figure wins (invariant #11). A
 * disagreement goes to a manager with both numbers rather than becoming an
 * unexplained variance at Day Close.
 *
 * Cash, card and online. No discount, void or split — each needs a manager PIN
 * verified server-side with bcrypt, and there is no server to ask.
 */
export function OfflinePaymentPrompt({
  open,
  ticketRef,
  price,
  onlineSubMethods = [],
  marketplaceLock,
  onClose,
  onTake,
  busy = false,
}: Props) {
  const [tender, setTender] = useState<OfflineTender>('cash')
  const [subMethod, setSubMethod] = useState('')
  const [tenderedText, setTenderedText] = useState('')
  const panelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    setTender(marketplaceLock ? 'online' : 'cash')
    setSubMethod(marketplaceLock?.providerSlug ?? '')
    setTenderedText('')
  }, [open, ticketRef, marketplaceLock])

  const { total, unknownLines } = useMemo(() => price(tender), [price, tender])

  const tendered = useMemo(() => {
    const n = Number.parseFloat(tenderedText)
    return Number.isFinite(n) ? n : null
  }, [tenderedText])

  // Change shows only once the note covers the bill. A negative "change" reads
  // as though the customer is owed money when they are in fact short.
  const change = tendered != null && tendered >= total ? tendered - total : null

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || busy) return
      e.preventDefault()
      e.stopPropagation()
      // Beat the counter's global window-level Escape handler, which would
      // otherwise close the checkout behind this sheet.
      e.stopImmediatePropagation()
      onClose()
    }
    document.addEventListener('keydown', onKey, { capture: true })
    return () => document.removeEventListener('keydown', onKey, { capture: true })
  }, [open, busy, onClose])

  if (!open) return null

  const needsProvider = tender === 'online' && onlineSubMethods.length > 0 && !subMethod && !marketplaceLock
  const blocked = busy || unknownLines > 0 || needsProvider

  const confirm = () => {
    if (blocked) return
    onTake({
      method: tender,
      amount: total,
      ...(tender === 'online' && subMethod ? { subMethod } : {}),
      ...(tender === 'cash' && tendered != null ? { tendered } : {}),
    })
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="offline-pay-title"
      // Clicking away closes. A sheet with no way out but one keyboard shortcut
      // is a trap on a touchscreen till.
      onMouseDown={(e) => {
        if (busy) return
        if (!panelRef.current?.contains(e.target as Node)) onClose()
      }}
    >
      <div
        ref={panelRef}
        // max-h + scroll: on a short window the buttons were BELOW the fold and
        // completely unreachable, with no way to dismiss the sheet.
        className="flex max-h-[88vh] w-full max-w-md flex-col overflow-hidden rounded-xl border border-border bg-card shadow-xl"
      >
        <div className="flex items-start gap-3 border-b border-border p-5 pb-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600">
            <WifiOff className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="offline-pay-title" className="text-base font-semibold leading-tight">
              Take payment — <span className="font-mono">{ticketRef}</span>
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Prints a slip that is <strong>not a tax invoice</strong>. The real invoice is
              filed when the connection returns.
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Close"
            onClick={onClose}
            disabled={busy}
            className="-mr-1 -mt-1 h-8 w-8 shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
          <div className="flex items-baseline justify-between rounded-lg border border-border bg-muted/40 px-4 py-3">
            <span className="text-sm text-muted-foreground">Amount due</span>
            <span className="text-2xl font-bold tabular-nums">{formatMoney(total)}</span>
          </div>

          {unknownLines > 0 && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {unknownLines} item{unknownLines === 1 ? '' : 's'} on this ticket{' '}
              {unknownLines === 1 ? 'is' : 'are'} not in this terminal&apos;s cached menu, so
              the total above is too low. Settle this order after reconnecting.
            </p>
          )}

          <div>
            <div className="mb-2 text-xs font-medium text-muted-foreground">
              Tender — the rate follows the tender, so the amount changes
            </div>
            <div className="grid grid-cols-3 gap-2">
              {TENDERS.map(({ id, label, Icon }) => (
                <Button
                  key={id}
                  type="button"
                  variant={tender === id ? 'default' : 'outline'}
                  onClick={() => setTender(id)}
                  disabled={busy || (marketplaceLock != null && id !== 'online')}
                  className="h-11 gap-1.5 text-sm"
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {label}
                </Button>
              ))}
            </div>
            {marketplaceLock ? (
              <p className="text-xs text-muted-foreground">
                {marketplaceLock.label} orders are paid by the platform — recorded as Online › {marketplaceLock.label} only.
              </p>
            ) : null}
          </div>

          {tender === 'cash' && (
            <div className="space-y-2">
              <label htmlFor="offline-tendered" className="text-sm font-medium">
                Cash received <span className="text-muted-foreground">(optional)</span>
              </label>
              <input
                id="offline-tendered"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={tenderedText}
                onChange={(e) => setTenderedText(e.target.value)}
                disabled={busy}
                className="h-11 w-full rounded-md border border-input bg-background px-3 text-base tabular-nums"
                placeholder={String(Math.ceil(total))}
              />
              {change != null && (
                <div className="flex items-baseline justify-between text-sm">
                  <span className="text-muted-foreground">Change to return</span>
                  <span className="font-semibold tabular-nums">{formatMoney(change)}</span>
                </div>
              )}
            </div>
          )}

          {tender === 'card' && (
            <p className="text-xs text-muted-foreground">
              Swipe on the bank terminal as usual. The POS records the tender but cannot
              confirm the swipe while offline — check it against the card batch.
            </p>
          )}

          {tender === 'online' && marketplaceLock && (
            <div className="space-y-2">
              <div className="text-sm font-medium">Provider</div>
              <Button type="button" size="sm" variant="default" disabled className="h-9 opacity-100">
                {marketplaceLock.label}
              </Button>
            </div>
          )}
          {tender === 'online' && !marketplaceLock && (
            <div className="space-y-2">
              {onlineSubMethods.length > 0 ? (
                <>
                  <div className="text-sm font-medium">Provider</div>
                  <div className="flex flex-wrap gap-2">
                    {onlineSubMethods.map((m) => (
                      <Button
                        key={m.slug}
                        type="button"
                        size="sm"
                        variant={subMethod === m.slug ? 'default' : 'outline'}
                        onClick={() => setSubMethod(m.slug)}
                        disabled={busy}
                        className="h-9"
                      >
                        {m.label}
                      </Button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Recorded so the online line still breaks down by provider in reports.
                  </p>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Confirm the transfer landed before handing the order over — the POS
                  cannot check it while offline.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="grid shrink-0 grid-cols-2 gap-3 border-t border-border p-5 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={busy}
            className="h-11 text-base"
          >
            Pay later
          </Button>
          <Button
            type="button"
            onClick={confirm}
            disabled={blocked}
            className={cn('h-11 text-base', blocked && 'cursor-not-allowed')}
          >
            {busy ? 'Printing…' : needsProvider ? 'Pick a provider' : 'Take payment'}
          </Button>
        </div>
      </div>
    </div>
  )
}
